import { useQuery } from '@tanstack/react-query';
import { api, AuditEntry } from '../lib/api';
import { relativeTime, absoluteTime } from '../lib/format';
import { ErrorState } from '../components/QueryState';

type DashboardData = {
  counts: { total: number; active24h: number; errored: number; blocked: number };
  recent_audit: AuditEntry[];
};

export function Dashboard() {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<DashboardData>('/dashboard'),
    refetchInterval: 30_000,
  });
  if (isError) return <ErrorState error={error} onRetry={() => refetch()} />;
  if (isLoading || !data) return <div className="text-muted">Loading…</div>;
  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold">Dashboard</h1>
      <div className="grid grid-cols-4 gap-4">
        <Stat label="Total clients" value={data.counts.total} />
        <Stat label="Active last 24h" value={data.counts.active24h} />
        <Stat label="Errored last run" value={data.counts.errored} tone={data.counts.errored ? 'danger' : undefined} />
        <Stat label="Blocked" value={data.counts.blocked} />
      </div>
      <section className="card">
        <h2 className="font-semibold mb-3">Recent admin activity</h2>
        <table className="table">
          <thead>
            <tr><th>When</th><th>Action</th><th>Target</th><th>Actor</th></tr>
          </thead>
          <tbody>
            {data.recent_audit.length === 0 && (
              <tr><td colSpan={4} className="text-muted">No activity yet.</td></tr>
            )}
            {data.recent_audit.map((e) => (
              <tr key={e.id}>
                <td title={absoluteTime(e.ts)}>{relativeTime(e.ts)}</td>
                <td className="font-mono">{e.action}</td>
                <td className="font-mono text-xs">{e.target || ''}</td>
                <td>{e.actor || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'danger' }) {
  return (
    <div className="card">
      <div className="text-sm text-muted">{label}</div>
      <div className={`text-3xl font-semibold mt-1 ${tone === 'danger' && value > 0 ? 'text-danger' : ''}`}>
        {value}
      </div>
    </div>
  );
}
